import {
  Node,
  ReactNodeViewRenderer,
  NodeViewWrapper,
  type ReactNodeViewProps,
} from '@tiptap/react';
import { EmojiRenderer } from '@/components/emoji-renderer/emoji-renderer';
import type { EmojiRef, EmojiVariant } from '@/components/emoji-renderer/types';
import { emojiShortcode } from '@/lib/emojis/emoji-manifest';
import styles from './emoji-extension.module.css';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    emoji: {
      /** Insert a sprite-rendered emoji at the current selection. */
      insertEmoji: (ref: EmojiRef) => ReturnType;
    };
  }
}

/** Inline size for an emoji in the text flow (px). */
const EMOJI_SIZE = 18;

/** React node view — renders the shared EmojiRenderer inside the editor. */
function EmojiNodeView({ node }: ReactNodeViewProps) {
  const group = (node.attrs.group as string) ?? '';
  const id = (node.attrs.id as string) ?? '';
  const variant = (node.attrs.variant as EmojiVariant) ?? 'default';
  return (
    <NodeViewWrapper as="span" data-emoji-view className={styles.wrapper}>
      <EmojiRenderer group={group} id={id} size={EMOJI_SIZE} variant={variant} alt={id} />
    </NodeViewWrapper>
  );
}

/**
 * Emoji — an inline atom node holding a sprite EmojiRef ({group, id, variant}),
 * rendered via the shared EmojiRenderer. Serializes to a `<span data-emoji-*>`
 * so it round-trips through the Yjs doc / HTML clipboard. Inserted via the
 * `insertEmoji` command (wired to the `:` trigger + EmojiPicker popover).
 */
export const EmojiExtension = Node.create({
  name: 'emoji',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      group: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-emoji-group') ?? '',
        renderHTML: (attrs) => ({ 'data-emoji-group': attrs.group as string }),
      },
      id: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-emoji-id') ?? '',
        renderHTML: (attrs) => ({ 'data-emoji-id': attrs.id as string }),
      },
      variant: {
        default: 'default',
        parseHTML: (el) => el.getAttribute('data-emoji-variant') ?? 'default',
        renderHTML: (attrs) => ({ 'data-emoji-variant': attrs.variant as string }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-emoji-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', { ...HTMLAttributes, 'data-emoji': '' }];
  },

  renderMarkdown(node) {
    return emojiShortcode(String(node.attrs?.group ?? ''), String(node.attrs?.id ?? ''));
  },

  addNodeView() {
    return ReactNodeViewRenderer(EmojiNodeView);
  },

  addCommands() {
    return {
      insertEmoji:
        ({ group, id, variant }: EmojiRef) =>
        ({ commands }: { commands: import('@tiptap/core').SingleCommands }) => {
          if (!group || !id) return false;
          return commands.insertContent({
            type: this.name,
            attrs: { group, id, variant },
          });
        },
    };
  },
});
